/**
 * scene_api.js – Scene create / update / read API helpers.
 *
 * Also registers window.saveScene for backward compatibility.
 */

import { getBaseUrl } from '../../envUtils';

// ---------------------------------------------------------------------------
// Environment detection
// ---------------------------------------------------------------------------

export function getEnvType() {
    const { origin } = window.location;
    if (origin === 'https://dev.thetwinmaster.com')   return 'dev';
    if (origin === 'https://arche.thetwinmaster.com') return 'arche';
    return 'arche';
}

// ---------------------------------------------------------------------------
// In-viewer building-scene renderer
// ---------------------------------------------------------------------------

/**
 * Replace the twinworld-app main area with a `<model-viewer>` for a GLB scene.
 * @param {{ local_glb_uri: string }} scene
 */
export const loadBuildingScene = (scene) => {
    document.querySelector('twinworld-app').shadowRoot.querySelector('main').innerHTML = `
        <model-viewer
            style="
                border: 2px solid rgb(243 111 75 / 56%);
                background: #363C4F 100% / cover no-repeat;
                width: calc(100vw - 64px);
                height: calc(100vh - 104px);
                margin-top: -24px;
            "
            src="${scene.local_glb_uri}"
            ar shadow-intensity="1" camera-controls touch-action="pan-y"
        />`;
};

// ---------------------------------------------------------------------------
// Scene save (window global – backward compatibility)
// ---------------------------------------------------------------------------

/**
 * Create or update a scene, preserving POI data when POIs haven't been
 * loaded in this session.
 *
 * @param {Object}  sceneData
 * @param {boolean} isNewScene
 * @returns {Promise<Object|string>}
 */
window.saveScene = async (sceneData) => {
    const shadowRoot = document.querySelector('twinworld-app').shadowRoot;
    shadowRoot.querySelector('.loaderbg').style.display = 'block';

    // ── Normalise building_id (caller uses 'buildingid') ─────────────────
    const buildingId = sceneData.building_id || sceneData.buildingid || '';

    // ── Normalise name (caller uses 'assetName') ──────────────────────────
    const name = sceneData.name || sceneData.assetName || '';

    // ── Normalise boundingBox ─────────────────────────────────────────────
    // Caller passes a radian object { north, south, east, west }; convert to
    // the degree string the API expects: "west,south,east,north"
    let boundingBoxStr = '';
    const bb = sceneData.boundingBox;
    if (bb && typeof bb === 'object') {
        const toDeg = (r) => (r * 180) / Math.PI;
        boundingBoxStr = `${toDeg(bb.west)},${toDeg(bb.south)},${toDeg(bb.east)},${toDeg(bb.north)}`;
    } else if (typeof bb === 'string') {
        boundingBoxStr = bb;
    }

    // ── Format body to match POST /api/v1/scene/ schema ──────────────────
    const payload = {
        name:        name,
        description: sceneData.description || '',
        building_id: buildingId,
        latitude:    parseFloat(sceneData.latitude)  || 0,
        longitude:   parseFloat(sceneData.longitude) || 0,
        attributes: [
            { id: 0, name: 'latitude',             value: String(sceneData.latitude  || ''), type: 'str' },
            { id: 0, name: 'longitude',            value: String(sceneData.longitude || ''), type: 'str' },
            { id: 0, name: 'buildingId',           value: buildingId, type: 'str' },
            { id: 0, name: 'buildingModelLocation',value: String(sceneData.buildingModelLocation || `${sceneData.longitude},${sceneData.latitude}`), type: 'str' },
            { id: 0, name: 'boundingBox',          value: boundingBoxStr, type: 'str' },
            { id: 0, name: 'height',               value: String(sceneData.height || sceneData.relative_height || '0'), type: 'str' },
            { id: 0, name: 'scale',                value: String(sceneData.scale  || '1.0'), type: 'str' },
            { id: 0, name: 'rotation',             value: String(sceneData.rotation  || sceneData.rotationZ || '0'), type: 'str' },
            { id: 0, name: 'angularRotation',      value: String(sceneData.angularRotation || '0'), type: 'str' },
            { id: 0, name: 'twin_provider',        value: String(sceneData.twin_provider || sceneData.twinProvider || 'cesium'), type: 'str' },
            { id: 0, name: 'sceneImage',           value: String(sceneData.sceneImage || ''), type: 'str' },
            { id: 0, name: 'building_glb',         value: String(sceneData.building_glb || sceneData.buildingModel || ''), type: 'str' },
        ]
    };

    console.log('💾 saveScene payload:', JSON.stringify(payload, null, 2));

    // ── API call ──────────────────────────────────────────────────────────
    try {
        const res  = await fetch(`${getBaseUrl()}/api/v2/scene/`, {
            method: 'POST',
            headers: {
                'accept': 'application/json',
                'Content-Type': 'application/json'
            },
            credentials: 'include',
            body: JSON.stringify(payload)
        });
        const body = await res.json();
        shadowRoot.querySelector('.loaderbg').style.display = 'none';
        return body;
    } catch (err) {
        shadowRoot.querySelector('.loaderbg').style.display = 'none';
        throw err;
    }
};
